import { Product } from "../../components/products/ProductInterface";

type EditField = "name" | "price" | "sku" | "country_code" | "currency_code";

type EditValues = {
  name: string;
  price: string;
  country_code: string;
  currency_code: string;
};

export type ProductState = {
  products: Product[];
  editingIds: Set<string>;
  editValuesMap: Record<string, EditValues>;
  hasChanges: boolean;
  isLoading: boolean;
};

export const initialProductState: ProductState = {
  products: [],
  editingIds: new Set(),
  editValuesMap: {},
  hasChanges: false,
  isLoading: false,
};

export type ProductAction =
  | { type: "INIT_FETCH_PRODUCTS" }
  | { type: "SET_PRODUCTS"; payload: Product[] }
  | { type: "SET_ERROR"; payload: string }
  | { type: "START_EDITING"; product: Product }
  | { type: "UPDATE_FIELD"; productId: string; field: EditField; value: string }
  | { type: "CANCEL_EDITING" }
  | { type: "SAVE_CHANGES" };

export function productsReducer(
  state: ProductState,
  action: ProductAction
): ProductState {
  switch (action.type) {
    case "INIT_FETCH_PRODUCTS":
      return { ...state, isLoading: true };

    case "SET_PRODUCTS":
      return { ...state, products: action.payload, isLoading: false };

    case "SET_ERROR":
      return { ...state, isLoading: false };

    case "START_EDITING": {
      const { product } = action;
      const newIds = new Set(state.editingIds).add(product.id);

      return {
        ...state,
        editingIds: newIds,
        editValuesMap: {
          ...state.editValuesMap,
          [product.id]: {
            name: product.name,
            price: product.price.toString(),
            country_code: product.country_code || "",
            currency_code: product.currency_code || "",
          },
        },
        hasChanges: true,
      };
    }

    case "UPDATE_FIELD": {
      const { productId, field, value } = action;
      return {
        ...state,
        editValuesMap: {
          ...state.editValuesMap,
          [productId]: {
            ...state.editValuesMap[productId],
            [field]: value,
          },
        },
        hasChanges: true,
      };
    }

    case "CANCEL_EDITING":
      return {
        ...state,
        editingIds: new Set(),
        editValuesMap: {},
        hasChanges: false,
      };

    case "SAVE_CHANGES": {
      Object.entries(state.editValuesMap).forEach(([productId, editValues]) => {
        const product = state.products.find((p) => p.id === productId);
        if (product) {
          product.name = editValues.name;
          product.price = parseFloat(editValues.price);
          product.country_code = editValues.country_code;
          product.currency_code = editValues.currency_code;
        }
      });
      return {
        ...state,
        editingIds: new Set(),
        editValuesMap: {},
        hasChanges: false,
      };
    }

    default:
      return state;
  }
}
